import type { FetchStatus } from '@tanstack/react-query'

import { formatDate, formatTime } from '@/lib/format'

/** What a view should show for a query right now. */
export type ViewState = 'loading' | 'failed' | 'offline' | 'empty' | 'loaded'

/**
 * The slice of a React Query result this reads. Structural, so a `useQuery`
 * result passes as it is.
 */
export interface ViewSource<T> {
  data: T | undefined
  isError: boolean
  fetchStatus: FetchStatus
  /**
   * How many times the query has settled in error. React Query puts a query
   * with no data back to `pending` the moment it refetches, so while a retry
   * runs this is the only trace that the last attempt failed.
   */
  errorUpdateCount?: number | undefined
}

export interface ViewOptions<T> {
  /** The data arrived with nothing in it: `(rows) => rows.length === 0`. */
  isEmpty?: ((data: T) => boolean) | undefined
  /** `useOnline()`. */
  online?: boolean | undefined
}

/**
 * The one ordering every screen should branch on, in place of
 * `isLoading ? … : isError ? … : data.length === 0 ? … : …`.
 *
 * 1. Data on screen wins. A background refetch that fails keeps what loaded;
 *    RefreshBar or UpdatingNote says it is stale. Checking `isError` first
 *    swaps real figures for an error block every time a poll misses.
 * 2. Offline next. React Query does not fail a fetch while the browser is
 *    offline, it pauses it: `pending` + `fetchStatus: 'paused'`, for as long
 *    as the device stays offline. Read as "loading" that is a skeleton
 *    forever, and read through `data ?? []` it is a false "nothing here".
 *    `online` covers the other half: a tab opened while offline, which React
 *    Query's `onlineManager` (it only hears the `online`/`offline` events)
 *    fetches and fails instead of pausing. Only `false` counts — see useOnline.
 * 3. Failed — including while a retry of that failure runs, so the "Try
 *    again" the member just pressed stays on screen (as "Trying…") instead of
 *    being swapped for a skeleton and taking their focus with it.
 * 4. Otherwise loading. Draw that through `useSkeletonTiming`
 *    (components/ui/use-skeleton-timing.ts) — QueryState and CardState
 *    already do — not straight onto the first render: a load that finishes
 *    inside 80ms, which a cached revisit always does, must paint nothing
 *    grey at all.
 *
 * A disabled query (`enabled: false`) with no data is `pending` + `idle`, so
 * it reads as loading: branch on the gate before calling this. For an
 * infinite query, `isEmpty` looks at the first page:
 * `(d) => d.pages[0]?.rows.length === 0`.
 */
export function resolveViewState<T>(
  source: ViewSource<T>,
  { isEmpty, online = true }: ViewOptions<T> = {},
): ViewState {
  if (source.data !== undefined) return isEmpty?.(source.data) === true ? 'empty' : 'loaded'
  if (!online || source.fetchStatus === 'paused') return 'offline'
  if (source.isError || (source.errorUpdateCount ?? 0) > 0) return 'failed'
  return 'loading'
}

const stamp = (at: number): string => {
  const iso = new Date(at).toISOString()
  return `${formatTime(iso)}, ${formatDate(iso)}`
}

/**
 * "Last updated 03:00 PM, 2 Bhadra 2083" for a failed state's meta line.
 * `null` for 0 — nothing has ever loaded, so there is nothing to date, and
 * the line is left out rather than printing the Unix epoch.
 */
export const lastUpdatedMeta = (dataUpdatedAt: number): string | null =>
  dataUpdatedAt > 0 ? `Last updated ${stamp(dataUpdatedAt)}` : null

/** "Saved at 03:00 PM, 2 Bhadra 2083" for an offline state showing saved data. `null` for 0. */
export const savedAtMeta = (savedAt: number): string | null =>
  savedAt > 0 ? `Saved at ${stamp(savedAt)}` : null
